import { getTypeColor } from '@/utils/nodeTypeMapper'; // Adjust the import path as necessary
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import ModeRoundedIcon from '@mui/icons-material/ModeRounded';
import MoveDownRoundedIcon from '@mui/icons-material/MoveDownRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import { Dialog, DialogContent } from '@mui/material';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import { useMemo, useState } from 'react';
import {
  buildTreeData,
  isNodeDescendant,
} from '../../utils/energyTreeFunctions';

import { useTranslation } from 'react-i18next';

import AddNodeTab from './AddNodeTab';
import DeleteNodeTab from './DeleteNodeTab';
import EditNodeTab from './EditNodeTab';
import InfoNodeTab from './InfoNodeTab';
import MoveNodeTab from './MoveNodeTab';

const ClickedNodeDialog = ({
  energyGroup,
  dialogOpen,
  clickedNode,
  handleClose,
  units,
  localData,
  setValue,
}) => {
  const [activeTab, setActiveTab] = useState('details');
  const { t } = useTranslation('energyxplorer-tree-tab', 'common');

  const decoratedUnits = useMemo(() => {
    const supportedUnitTypeIds = [28, 30, 61];
    const map = new Map();
    localData.forEach((node) => {
      node?.dataStreams?.forEach((stream) => {
        if (stream.unitId != null) {
          map.set(stream.unitId, node.name);
        }
      });
    });

    return units?.map((unit) => {
      const usedInNode = map.get(unit.UnitId) || null;
      const isSupported = supportedUnitTypeIds.includes(
        unit.UnitType?.UnitTypeId,
      );
      const notSupportedReason = !isSupported ? 'Understøttes ikke' : null;

      return {
        ...unit,
        usedInNode,
        notSupportedReason,
        isDisabled: Boolean(usedInNode || notSupportedReason),
      };
    });
  }, [localData, units]);

  const treeOptions = useMemo(() => {
    const filtered = localData.filter(
      (n) =>
        n.energyTreeNodeId !== clickedNode?.energyTreeNodeId &&
        !isNodeDescendant(
          n.energyTreeNodeId,
          clickedNode?.energyTreeNodeId,
          localData,
        ),
    );
    return buildTreeData(filtered);
  }, [localData, clickedNode]);

  const getAncestors = (nodeId) => {
    const map = new Map(localData.map((n) => [n.energyTreeNodeId, n]));
    const ancestors = [];
    let current = map.get(nodeId);
    while (current?.parentId != null) {
      const parent = map.get(current.parentId);
      if (!parent) break;
      ancestors.push(parent);
      current = parent;
    }
    return ancestors;
  };

  const isMainMeter = clickedNode?.type === 'Hovedmåler';

  const isMainMeterUsed = useMemo(() => {
    return localData.some((n) => n.type === 'Hovedmåler');
  }, [localData]);

  const handleDialogClose = () => {
    setActiveTab('details');
    handleClose();
  };

  return (
    <Dialog
      open={dialogOpen}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="md"
      sx={{
        '& .MuiDialog-paper': {
          border: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <BottomNavigation
        showLabels
        disableRipple
        value={activeTab}
        onChange={(event, newValue) => setActiveTab(newValue)}
      >
        <BottomNavigationAction
          disableRipple
          label={t('tree_actions.view_details')}
          value="details"
          icon={<QueryStatsRoundedIcon />}
          sx={{
            '&.Mui-selected': {
              borderBottom: '3px solid',
              borderColor: getTypeColor(clickedNode?.type),
              borderRadius: 0,
            },
          }}
        />

        <BottomNavigationAction
          disableRipple
          label={t('tree_actions.edit_node')}
          value="edit"
          // disabled={isResidualNode}
          icon={
            // <Badge
            //   overlap="circular"
            //   anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            //   badgeContent={
            //     isResidualNode ? (
            //       <ClearRoundedIcon fontSize="small" color="error" />
            //     ) : null
            //   }
            // >
            <ModeRoundedIcon />
            // </Badge>
          }
          sx={{
            '&.Mui-selected': {
              borderBottom: '3px solid',
              borderColor: getTypeColor(clickedNode?.type),
              borderRadius: 0,
            },
          }}
        />

        {clickedNode?.type !== 'Hovedmåler' && (
          <BottomNavigationAction
            disableRipple
            label={t('tree_actions.move_node')}
            // disabled={isResidualNode}
            value="move"
            icon={
              // <Badge
              //   overlap="circular"
              //   anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              //   badgeContent={
              //     isResidualNode ? (
              //       <ClearRoundedIcon fontSize="small" color="error" />
              //     ) : null
              //   }
              // >
              <MoveDownRoundedIcon />
              // </Badge>
            }
            sx={{
              '&.Mui-selected': {
                borderBottom: '3px solid',
                borderColor: getTypeColor(clickedNode?.type),
                borderRadius: 0,
              },
            }}
          />
        )}

        <BottomNavigationAction
          disableRipple
          label={t('tree_actions.add_node')}
          value="add"
          // disabled={isResidualNode}
          icon={<AddRoundedIcon />}
          sx={{
            '&.Mui-selected': {
              borderBottom: '3px solid',
              borderColor: getTypeColor(clickedNode?.type),
              borderRadius: 0,
            },
          }}
        />

        <BottomNavigationAction
          disableRipple
          label={t('tree_actions.delete_node')}
          value="delete"
          icon={<DeleteRoundedIcon />}
          sx={{
            '&.Mui-selected': {
              borderBottom: '3px solid',
              borderColor: getTypeColor(clickedNode?.type),
              borderRadius: 0,
            },
          }}
        />
      </BottomNavigation>

      <DialogContent dividers>
        {activeTab === 'edit' && (
          <EditNodeTab
            clickedNode={clickedNode}
            decoratedUnits={decoratedUnits}
            localData={localData}
            isMainMeter={isMainMeter}
            handleClose={handleDialogClose}
          />
        )}

        {activeTab === 'move' && (
          <MoveNodeTab
            clickedNode={clickedNode}
            treeOptions={treeOptions}
            tree={localData}
            isMainMeterUsed={isMainMeterUsed}
            handleClose={handleDialogClose}
          />
        )}

        {activeTab === 'add' && (
          <AddNodeTab
            clickedNode={clickedNode}
            energyGroup={energyGroup}
            tree={localData}
            decoratedUnits={decoratedUnits}
            getAncestors={getAncestors}
            isMainMeterUsed={isMainMeterUsed}
            handleClose={handleDialogClose}
          />
        )}

        {activeTab === 'delete' && (
          <DeleteNodeTab
            clickedNode={clickedNode}
            handleClose={handleDialogClose}
          />
        )}

        {activeTab === 'details' && (
          <InfoNodeTab clickedNode={clickedNode} setValue={setValue} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClickedNodeDialog;
